import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { getAuthUser } from "../../services/auth";
import "./Lead.css";
import { Button } from "primereact/button";

export default function LeadTable({
  leads,
  onEdit,
  onDelete,
  tableLoading,
  page,
  perPageSize,
  search,
}) {
  const auth = getAuthUser();

  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const startedFetchRef = useRef(false);

  useEffect(() => {
    if (tableLoading) startedFetchRef.current = true;

    if (!tableLoading && startedFetchRef.current) {
      setHasFetchedOnce(true);
    }
  }, [tableLoading]);

  const showSkeleton = tableLoading || !hasFetchedOnce;

  /* =======================
     SKELETON SETUP
     ======================= */
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: perPageSize }, (_, i) => ({
        id: `skeleton-${i}`,
      })),
    [perPageSize]
  );

  const skeletonBody = () => (
    <Skeleton width="100%" height="1.4rem" borderRadius="10px" />
  );

  /* =======================
     HIGHLIGHT LOGIC (SAMA)
     ======================= */
  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, keyword) => {
    if (!keyword) return text || "-";

    keyword = keyword.replace(/[^\p{L}\p{N}]+/gu, " ");
    const keywords = Array.from(
      new Set(keyword.trim().split(/\s+/).filter(Boolean))
    ).map(escapeRegex);

    if (!keywords.length) return text || "-";

    const regex = new RegExp(`(${keywords.join("|")})`, "gi");

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: String(text || "-").replace(
            regex,
            `<span class="highlight-lead-table">$1</span>`
          ),
        }}
      />
    );
  };

  /* =======================
     COLUMN BODY
     ======================= */
  const numberBody = (_, options) =>
    showSkeleton
      ? skeletonBody()
      : options.rowIndex + 1 + (page - 1) * perPageSize;

  const textBody =
    (field, maxWidth = "200px") =>
    (row) =>
      showSkeleton ? (
        skeletonBody()
      ) : (
        <div className="text-wrap text-break" style={{ maxWidth }}>
          {highlightText(row[field], search)}
        </div>
      );

  const phoneBody = (row) => {
    if (showSkeleton) return skeletonBody();

    const encodedPhone = row.phone_number;
    if (!encodedPhone) return <span className="text-muted">-</span>;

    const handleClick = () => {
      let decoded = "";
      try {
        decoded = atob(encodedPhone);
      } catch {
        alert("Nomor WA tidak valid");
        return;
      }

      const clean = decoded.replace(/\D/g, "");
      const wa = clean.startsWith("0") ? "62" + clean.slice(1) : clean;

      window.open(`https://wa.me/${wa}`, "_blank", "noopener,noreferrer");
    };

    return (
      <button
        onClick={handleClick}
        className="btn btn-link text-success d-flex align-items-center gap-1 text-decoration-none"
      >
        <i className="fa-brands fa-whatsapp fs-3"></i>
        <span>Whatsapp</span>
      </button>
    );
  };

  const positionBody = (row) =>
    showSkeleton
      ? skeletonBody()
      : highlightText(row.position?.initials || "-", search);

  const actionBody = (row) =>
    showSkeleton ? (
      skeletonBody()
    ) : (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          className="action-btn edit-btn mr-2 rounded"
          rounded
          outlined
          onClick={() => onEdit(row)}
        />
        <Button
          icon="pi pi-trash"
          className="action-btn delete-btn rounded"
          rounded
          outlined
          onClick={() => onDelete(row.id)}
        />
      </React.Fragment>
    );

  const shouldShowEmpty = hasFetchedOnce && !tableLoading && leads.length === 0;

  return (
    <DataTable
      value={showSkeleton ? skeletonRows : leads}
      loading={false}
      scrollable
      scrollHeight="543px"
      showGridlines
      frozenHeader
      rowHover
      responsiveLayout="scroll"
      emptyMessage={shouldShowEmpty ? "Tidak ada data" : null}
      className="p-datatable-sm lead-datatable"
    >
      <Column
        header="No"
        body={numberBody}
        style={{ width: "60px", textAlign: "center", cursor: "pointer" }}
      />

      <Column
        header="Nama"
        body={textBody("name")}
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="No Telp"
        body={phoneBody}
        style={{ minWidth: "160px", textAlign: "center", cursor: "pointer" }}
      />

      <Column
        header="KLPD"
        body={textBody("klpd")}
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Jabatan"
        body={positionBody}
        style={{ minWidth: "160px", cursor: "pointer" }}
      />

      <Column
        header="Instansi / Satker"
        body={textBody("institution_or_business")}
        style={{ minWidth: "220px", cursor: "pointer" }}
      />

      <Column
        header="Lokasi"
        body={textBody("location", "300px")}
        style={{ minWidth: "260px", cursor: "pointer" }}
      />

      {auth?.role === "admin" && (
        <Column
          header="Aksi"
          body={actionBody}
          frozen
          alignFrozen="right"
          style={{ minWidth: "150px", textAlign: "center", cursor: "pointer" }}
        />
      )}
    </DataTable>
  );
}
